import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import { ol } from './helpers';
import { EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES } from '../Sections/ExecutiveSummary/CompanyDescriptionExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES } from '../Sections/ExecutiveSummary/FinancialInformationExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES } from '../Sections/ExecutiveSummary/FinancialNeedsExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_MARKET_NAMES } from '../Sections/ExecutiveSummary/MarketExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_PRODUCTS_OR_SERVICES_NAMES } from '../Sections/ExecutiveSummary/ProductsExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_RISKS_NAMES } from '../Sections/ExecutiveSummary/RisksExecSummary/utils/form';
import { EXECUTIVE_SUMMARY_ORGANIZATION_AND_TEAM_NAMES } from '../Sections/ExecutiveSummary/TeamExecSummary/utils/form';

const COMPANY_DESCRIPTION_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.HISTORY]: {
    name: 'History of the Business',
    instruction:
      'Briefly introduce your business`s founding and early milestones, including the market gap that sparked its creation. Highlight initial achievements like company registration, license acquisition, or prototype development.',
    exampleOne:
      'In 2020, the Company launched its first tracked tractor model, revolutionizing farming in challenging soils with superior traction and flotation.',
    exampleTwo:
      'In 2021, the language platform launched its AI-powered translation feature, revolutionizing multilingual communication.',
  },
  [EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.RESOURCES_AND_ASSETS]: {
    name: 'Main Resources and Assets',
    instruction:
      'Categorize company assets: physical facilities, equipment, inventory, and intangible assets like tech, IP, and brand equity; evaluate them',
    exampleOne:
      'The primary resources and assets of the company include state-of-the-art manufacturing facilities, cutting-edge technology and machinery, a skilled workforce, intellectual property, and strategic partnerships. The company`s manufacturing facilities have advanced production lines and quality control systems, allowing us to produce high-quality agricultural machinery efficiently.',
    exampleTwo:
      'The company`s main resources and assets include advanced AI technology, a diverse network of skilled translators, robust security protocols, customer relationships, and intellectual property rights.',
  },
  [EXECUTIVE_SUMMARY_COMPANY_DESCRIPTION_NAMES.COMPANY_OWNERS]: {
    name: 'Owners of the Company',
    instruction:
      "Ensure to provide the complete legal name of the company's owners. Double-check the spelling and format to avoid any errors.",
    exampleOne: '1.John Johnson \n 2.Oliver Smith',
    exampleTwo: '1.John Doe',
  },
};

const PRODUCTS_OR_SERVICES_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_PRODUCTS_OR_SERVICES_NAMES.PRODUCT_VALUE]: {
    name: 'Product/Service Value and Problem Solved',
    instruction:
      'Clearly state the primary value proposition of your product and its problem-solving capabilities, elaborating on customer benefits and practical impact.',
    exampleOne:
      'By minimizing soil compaction and maximizing traction, the tracked tractors enable farmers to work more effectively, completing tasks faster and more precisely.',
    exampleTwo:
      'The language services platform facilitates seamless communication across linguistic barriers, enabling clients to engage with global audiences and expand their reach effectively.',
  },
};

const MARKET_AND_CUSTOMERS_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_MARKET_NAMES.DYNAMICS]: {
    name: 'Market Dynamics',
    instruction:
      'Outline market dynamics: forces influencing supply, demand, competition, industry structure. Identify target market CAGR. Discuss current trends: shifts in consumer preferences, tech, best practices. Highlight business impact and adaptability.',
    exampleOne:
      'Over the past decade, the market has experienced a compound annual growth rate (CAGR) of approximately 5%, reaching a total value of over $200 billion in 2020. This growth is driven by increasing mechanization in developing countries, where the adoption of modern farming practices has accelerated to meet rising food demand.',
    exampleTwo:
      'The language services market is experiencing significant growth driven by globalization, digitalization, and increasing cross-border communication. Key trends include rising demand for AI-powered translation solutions, growth in emerging markets, adoption of cloud-based language platforms, and focus on language diversity and localization.',
  },
  [EXECUTIVE_SUMMARY_MARKET_NAMES.COMPETITION_PLAN]: {
    name: 'Competition Strategy',
    instruction:
      'Present competitive strategy: how to distinguish offerings from competitors. Highlight unique selling propositions: innovation, pricing, customer experience. Discuss market positioning and guiding principles.',
    exampleOne:
      'The company invests heavily in research and development to continuously enhance product offerings, ensuring they remain at the forefront of technological advancements and address evolving customer needs. The company prioritizes customer satisfaction by providing exceptional service and support throughout the entire customer lifecycle.',
    exampleTwo:
      'The company plans to differentiate ourselves by focusing on innovation, superior technology, personalized service, and agility. By continuously improving an AI technology, expanding language support, and delivering exceptional customer experiences, the company aims to stay ahead of competitors and maintain its position as a leader in the language services industry.',
  },
};

const ORGANIZATION_AND_TEAM_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_ORGANIZATION_AND_TEAM_NAMES.MEMBERS]: {
    name: 'Core Team Members',
    instruction: 'List core team members',
    exampleOne:
      'In the Company the critical project participants include John Johnson, Oliver Smith, Isabella Brown and Henry Miller.',
    exampleTwo: 'In the Company the critical project participants include John Johnson, Mary Wilson and Liam Anderson.',
  },
};

const FINANCIAL_INFORMATION_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.AVERAGE_ANNUAL_PROFIT]: {
    name: 'Past Annual Profit',
    instruction:
      'Calculate and provide the average annual profit by summing your net profit for the past three years and dividing by three. This figure should be accurate and reflect your financial performance consistently over the period.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.ASSETS_SIZE]: {
    name: 'Size of Your Assets',
    instruction:
      'To calculate the size of your assets:' +
      ol([
        'List all assets including cash, investments, property, and equipment.',
        'Determine the current market value of each asset.',
        'Sum the values to get the total asset size.',
      ]) +
      'Utilize the formula: Total Assets = Cash + Investments + Property + Equipment + Other Assets.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.PROJECTED_ANNUAL_PROFIT]: {
    name: 'Projected Annual Profit',
    instruction:
      'To calculate projected annual profit for the next three years:' +
      ol([
        'Forecast yearly revenue based on sales and pricing trends.',
        'Subtract COGS to determine gross profit.',
        'Deduct operating expenses, taxes, and interest.',
        'Factor in expected growth, market conditions, and planned investments or cost changes.',
      ]),
    exampleOne: '',
    exampleTwo: '',
  },
  [EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.RETURN_ON_INVESTMENT]: {
    name: 'Return on Investment',
    instruction:
      'To calculate Return on Investment (ROI):' +
      ol([
        'Determine the net profit from the investment.',
        'Divide the net profit by the initial investment cost.',
        'Multiply by 100 to express as a percentage.',
      ]) +
      'Utilize the formula: ROI = (Net Profit / Initial Investment Cost) * 100.',
    exampleOne: '',
    exampleTwo: '',
  },
  [EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.INTERNAL_RATE_OF_RETURN]: {
    name: 'Internal Rate of Return ',
    instruction:
      'To calculate the Internal Rate of Return (IRR):' +
      ol([
        'Gather initial investment and cash flow data.',
        'Determine the rate that makes the net present value (NPV) of cash flows equal to zero.',
        'Use financial software to find the IRR.',
      ]),
    exampleOne: '',
    exampleTwo: '',
  },
};

const FUNDING_DIRECTIONS_INSTRUCTIONS = {
  name: 'Funding directions',
  instruction: 'Specify budget allocations for each purpose: R&D, Marketing, Working Capital, Construction, Equipment.',
  exampleOne: '',
  exampleTwo: '',
};

const FINANCING_NEEDS_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.RESEARCH_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.MARKETING_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.WORKING_CAPITAL_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.CONSTRUCTION_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.EQUIPMENT_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
  [EXECUTIVE_SUMMARY_FINANCING_NEEDS_NAMES.OTHER_COST]: {
    currencyAmount: FUNDING_DIRECTIONS_INSTRUCTIONS,
    percentage: FUNDING_DIRECTIONS_INSTRUCTIONS,
  },
};

const RISKS_INSTRUCTIONS = {
  [EXECUTIVE_SUMMARY_RISKS_NAMES.MAIN_RISKS]: {
    name: 'Main Project Risks',
    instruction:
      'Outline main project risks: market dynamics, tech challenges, regulatory changes, financial uncertainties. Assess impact of each risk on project success.',
    exampleOne: ol([
      'Unexpected technical complexities during the development process.',
      'Market volatility and shifting customer preferences present another risk, as changes in economic conditions, regulatory policies, or competitive dynamics could impact demand for the product or alter market dynamics.',
    ]),
    exampleTwo:
      'The main risks to our project include technological disruptions, cybersecurity threats, market volatility, regulatory changes, and competition. Additionally, reliance on external vendors or talent shortages may pose challenges. ',
  },
  [EXECUTIVE_SUMMARY_RISKS_NAMES.MEASURES]: {
    name: 'Risk Mitigation Measures',
    instruction:
      'Detail risk mitigation measures: diversification, insurance, contingency funds, partnerships. Highlight ongoing risk monitoring and team adaptability. Reinforce effectiveness of risk mitigation strategy.',
    exampleOne: ol([
      'The company employs robust risk management strategies, conducts thorough market research, and maintains open communication channels with stakeholders to identify potential issues early and implement proactive measures to address them.',
      'The company maintain contingency plans and flexibility in the project approach to adapt to changing circumstances and ensure successful project outcomes.',
    ]),
    exampleTwo:
      'Mitigation strategies include:' +
      ol([
        'Diversifying revenue streams',
        'Staying updated on industry trends',
        'Maintaining robust security measures',
      ]),
  },
};

export const EXECUTIVE_SUMMARY_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.COMPANY_DESCRIPTION]: COMPANY_DESCRIPTION_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.PRODUCTS_OR_SERVICES]: PRODUCTS_OR_SERVICES_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.MARKET_AND_CUSTOMERS]: MARKET_AND_CUSTOMERS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.ORGANIZATION_AND_TEAM]: ORGANIZATION_AND_TEAM_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.FINANCIAL_INFORMATION]: FINANCIAL_INFORMATION_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.FINANCING_NEEDS]: FINANCING_NEEDS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.EXECUTIVE_SUMMARY.RISKS]: RISKS_INSTRUCTIONS,
};
